import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock.es6';
import * as focusTrap from 'focus-trap';
import style from './modal.css';
import '../button/button.js';
import '../icon/icon.js';

/**
 * Modal component to make a dialog declarative and accessible
 * @property {boolean} previewMode
 * @property {boolean} deepLinking
 * @property {boolean} autoOpen
 * @property {boolean} isOpen
 * @property {'top' | 'center' | 'bottom' | 'fullscreen'} placement
 * @property {string} label
 * @property {string} closeLabel
 */
export default class Modal extends TemplateElement {
    constructor() {
        super({ styles: [style], shadowRender: true });
    }

    properties() {
        return {
            previewMode: false,
            deepLinking: true,
            autoOpen: false,
            isOpen: false,
            placement: 'top',
            label: '',
            closeLabel: '',
        };
    }

    connected() {
        // TODO: handle case with no id
        const triggers = document.querySelectorAll(`[show-modal="${this.id}"]`);
        for (const trigger of triggers) {
            trigger.addEventListener('click', (event) => {
                this.show();
            });
        }

        this.focusTrap = focusTrap.createFocusTrap([this, this.$refs.dialog], {
            fallbackFocus: this,
        });

        if ((this.deepLinking && window.location.hash.includes(this.id)) || this.autoOpen) {
            setTimeout(() => {
                this.show();
            }, 0);
        }

        setTimeout(() => {
            this.adoptStyleSheets();
        }, 100);
    }

    watch() {
        return {
            isOpen: (open) => {
                if (open) {
                    this.$refs.dialog.showModal();
                    this.dispatch('modal-open', true, true, true);
                    if (this.deepLinking) {
                        window.location.hash = this.id;
                    }
                    //TODO disableBodyScroll and focusTrap dont work
                    disableBodyScroll(this.$refs.dialog, {
                        reserveScrollBarGap: true,
                    });
                    this.focusTrap.activate();
                } else {
                    this.$refs.dialog.close();
                    this.dispatch('modal-close', true, true, true);
                    const [url, hash] = window.location.href.split('#');
                    window.history.pushState(null, null, url);
                    enableBodyScroll(this.$refs.dialog);
                    this.focusTrap.deactivate();
                }
            },
        };
    }

    events() {
        return {
            window: {
                hashchange: () => {
                    if (window.location.hash.substring(1) === this.id && !this.isOpen) {
                        this.show();
                    } else if (!window.location.hash.substring(1) === this.id && this.isOpen) {
                        this.close();
                    }
                },
            },
            dialog: {
                close: () => {
                    this.isOpen = false;
                },
                cancel: () => {
                    this.isOpen = false;
                },
                click: (event) => {
                    if (event.target.nodeName === 'DIALOG') {
                        this.$refs.dialog.close('dismiss');
                    }
                },
            },
            '[close-modal]': {
                click: () => {
                    this.close();
                },
            },
        };
    }

    show() {
        this.isOpen = true;
    }

    close() {
        this.isOpen = false;
    }

    template() {
        return html`
            <dialog ref="dialog" placement="${this.placement}" preview-mode="${this.previewMode}">
                <div ref="content" class="content">
                    <header>
                        <slot name="header" ref="headerSlot">
                            ${when(this.label, html` <h2>${this.label}</h2> `)}
                        </slot>
                    </header>
                    <main>
                        <slot ref="mainSlot"></slot>
                    </main>
                    <footer>
                        <slot name="footer" ref="footerSlot"> </slot>
                    </footer>
                    <button class="Modal--closeBtn" close-modal>
                        ${this.closeLabel}
                        <div class="CloseIcon"></div>
                    </button>
                </div>
            </dialog>
        `;
    }
}

defineElement('mb-modal', Modal);
